import {computed, ref, watch} from "vue";
import {useInputFormatter} from "./InputFormatter";
import {twMerge} from "tailwind-merge";

export const useInput = (props, emit) => {

  const id = "input-" + Math.random().toString(36).substr(2, 9);
  const inputEl = ref(null);
  const showPassword = ref(false);
  const isFocused = ref(false);

  const formatter = useInputFormatter(props.format, {
    maxLength: props.maxLength,
    ...props.format === "usd" && {"allowDecimal": true},
    prefixes: props.prefixes || [],
    getPrefix: () => props.prefix,
  });

  const cleaned = ref(props.modelValue);
  const formatted = computed(() => formatter.applyFormat(cleaned.value, isFocused.value));
  const currentType = computed(() => props.type !== "password" ? props.type : showPassword.value ? "text" : "password");

  const inputClassesComputed = computed(() => {
    const classes = [
      props.error ? "border-red-500 focus:shadow-outline shadow" : !props.dark ? "border-slate-200" : "",
      props.roundedFull ? "rounded-full px-5" : props.notRoundedLeft ? "rounded-r pl-4" : "rounded-lg pl-4",
      props.readonly && !props.copy && "cursor-not-allowed",
      props.copy && "cursor-copy",
      props.type === "password" || props.copy || props.type === "search" ? "pr-12" : "pr-4",
      props.dark ? "bg-[#343a40]" : "text-slate-900 bg-slate-100 border",
    ];

    return twMerge(classes, props.inputClasses);
  });

  const copyToClipboard = () => {
    if (navigator.clipboard.writeText(props.modelValue)) toast.success("Copied to clipboard!");
    else toast.warning("Hmm.. this browser does not support copying to clipboard.");
  };

  const enforceFormat = (e) => {
    if (props.readonly) return;

    if (e.clipboardData) {
      const pastedText = e.clipboardData.getData("text/plain");
      e.preventDefault();
      const pasted = formatter.clean(pastedText);
      if (pasted) {
        cleaned.value = pasted;
        emit("update:modelValue", cleaned.value);
      }
      return;
    }

    formatter.enforceFormat(e, cleaned.value ? cleaned.value.length : 0);
  };

  const emitEvent = (emitName, e) => {
    if (props.readonly) return;
    if (emitName === "change") emitEvent("update:modelValue", e);
    cleaned.value = formatter.clean(e.target.value);
    emit(emitName, cleaned.value);
  };

  const inputBlur = (e) => {
    if (props.readonly) return;
    isFocused.value = false;
    emitEvent("input-blur", e);
  };

  const inputFocus = (e) => {
    if (props.readonly) return;
    isFocused.value = true;
    emitEvent("input-focus", e);
  };

  const inputMode = computed(() => {
    if (props.inputMode) return props.inputMode;
    if (props.type === "search") return "search";
    if (!props.format) return "text";
    if (props.format === "usd") return "decimal";
    if (props.format === "integer") return "numeric";
    if (props.format === "phone") return "tel";
    if (props.format === "email") return "email";
    if (props.format === "percentage") return "decimal";

    return "text";
  });

  const handleAction = () => {
    if (props.copy) copyToClipboard();
  };

  watch(() => props.modelValue, () => {
    cleaned.value = props.modelValue;
    formatter.applyFormat(cleaned.value);
  });

  watch(() => props.prefix, () => {
    cleaned.value = formatter.clean(props.modelValue);
    formatter.applyFormat(cleaned.value);
    emit("update:modelValue", cleaned.value);
  });

  return {
    id,
    inputEl,
    showPassword,
    cleaned,
    formatted,
    currentType,
    inputClassesComputed,
    inputMode,
    isFocused,
    handleAction,
    enforceFormat,
    inputBlur,
    inputFocus,
    emitEvent,
  };
};
